/**
 * useChatAttachments Hook
 * Handles file uploads for chat messages
 */

import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

export function useChatAttachments(bookingId, threadId = null) {
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(null)

  /**
   * Validate file before upload
   */
  const validateFile = useCallback((file) => {
    // Check file exists
    if (!file) {
      return { valid: false, error: 'No file provided' }
    }

    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: 'Invalid file type. Only JPEG, PNG, and PDF files are allowed.'
      }
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2)
      return {
        valid: false,
        error: `File size (${sizeMB}MB) exceeds the 10MB limit.`
      }
    }

    return { valid: true }
  }, [])

  /**
   * Upload attachment to Supabase Storage
   * Returns attachment metadata
   */
  const uploadAttachment = useCallback(async (file, messageId) => {
    if (!bookingId) {
      return {
        success: false,
        error: 'Booking ID is required'
      }
    }

    if (!messageId) {
      return {
        success: false,
        error: 'Message ID is required'
      }
    }

    // Validate file
    const validation = validateFile(file)
    if (!validation.valid) {
      setError(validation.error)
      return {
        success: false,
        error: validation.error
      }
    }

    setIsUploading(true)
    setProgress(0)
    setError(null)

    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      // Create FormData
      const formData = new FormData()
      formData.append('file', file)

      // Upload via API endpoint
      const response = await fetch(`/api/messages/${messageId}/attachment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.message || 'Failed to upload attachment')
      }

      setProgress(100)
      setIsUploading(false)

      return {
        success: true,
        attachment_url: result.attachment_url,
        attachment_type: result.attachment_type,
        attachment_name: result.attachment_name,
        attachment_size_bytes: result.attachment_size_bytes
      }
    } catch (err) {
      console.error('Error uploading attachment:', err)
      setError(err.message)
      setIsUploading(false)
      setProgress(0)

      return {
        success: false,
        error: err.message
      }
    }
  }, [bookingId, validateFile])

  /**
   * Send a message with an attachment
   * Creates the message first, then uploads the attachment
   */
  const sendMessageWithAttachment = useCallback(async (messageText, file) => {
    if (!threadId) {
      return {
        success: false,
        error: 'Please select a conversation before sending an attachment.'
      }
    }

    if (!file) {
      return {
        success: false,
        error: 'No file provided'
      }
    }

    try {
      // Validate file
      const validation = validateFile(file)
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error
        }
      }

      setIsUploading(true)
      setProgress(10)
      setError(null)

      // Step 1: Create the message
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      setProgress(30)

      const createResponse = await fetch(`/api/messages/bookings/${bookingId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          message_text: messageText || 'Attachment',
          thread_id: threadId,
        })
      })

      const createResult = await createResponse.json()

      if (!createResponse.ok) {
        throw new Error(createResult.message || 'Failed to create message')
      }

      const messageId = createResult.message.id

      setProgress(50)

      // Step 2: Upload attachment
      const uploadResult = await uploadAttachment(file, messageId)

      if (!uploadResult.success) {
        // Delete the message if upload failed
        // Note: We could add a DELETE endpoint, but for now just leave it
        throw new Error(uploadResult.error)
      }

      setProgress(100)
      setIsUploading(false)

      return {
        success: true,
        message: createResult.message,
        attachment: uploadResult
      }
    } catch (err) {
      console.error('Error sending message with attachment:', err)
      setError(err.message)
      setIsUploading(false)
      setProgress(0)

      return {
        success: false,
        error: err.message
      }
    }
  }, [bookingId, threadId, validateFile, uploadAttachment])

  /**
   * Reset upload state
   */
  const reset = useCallback(() => {
    setIsUploading(false)
    setProgress(0)
    setError(null)
  }, [])

  return {
    uploadAttachment,
    sendMessageWithAttachment,
    validateFile,
    isUploading,
    progress,
    error,
    reset,
    // Export constants for UI
    ALLOWED_TYPES,
    MAX_FILE_SIZE
  }
}
